import React, { useState, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './vehicle-details.css';
import './styles/design-system.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import { DEMO_VEHICLES } from './config/brandConfig';
import dataManager from './utils/dataManager';

const Homepage = lazy(() => import('./components/Homepage'));
const VehicleList = lazy(() => import('./components/VehicleList'));
const VehicleDetails = lazy(() => import('./components/VehicleDetails'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
const CashCars = lazy(() => import('./components/CashCars'));
const LeaseCars = lazy(() => import('./components/LeaseCars'));
const LeaseCalculator = lazy(() => import('./components/LeaseCalculator'));
const Favorites = lazy(() => import('./components/Favorites'));
const TestDrive = lazy(() => import('./components/TestDrive'));
const About = lazy(() => import('./components/About'));
const Contact = lazy(() => import('./components/Contact'));

function ConditionalFooter() {
  const location = useLocation();
  const hideFooterPaths = ['/vehicle/'];
  const shouldHideFooter = hideFooterPaths.some(path => location.pathname.includes(path));
  return shouldHideFooter ? null : <Footer />;
}

function App() {
  const [user, setUser] = useState(() => {
    return dataManager.userManager.getCurrentUser();
  });
  
  const [vehicles, setVehicles] = useState(() => {
    const stored = dataManager.vehicleManager.getAll();
    if (stored.length === 0) {
      // Initialize with demo vehicles if no data exists
      DEMO_VEHICLES.forEach(vehicle => {
        dataManager.vehicleManager.add(vehicle);
      });
      return DEMO_VEHICLES;
    }
    return stored;
  });
  
  const [inquiries, setInquiries] = useState(() => {
    return dataManager.inquiryManager.getAll();
  });
  
  const [savedVehicles, setSavedVehicles] = useState(() => {
    return dataManager.favoritesManager.getAll();
  });

  const [testDrives, setTestDrives] = useState(() => {
    return dataManager.testDriveManager.getAll();
  });

  const [leaseApplications, setLeaseApplications] = useState(() => {
    return dataManager.leaseManager.getAll();
  });

  // Refresh data from localStorage when needed
  const refreshData = useCallback(() => {
    setVehicles(dataManager.vehicleManager.getAll());
    setInquiries(dataManager.inquiryManager.getAll());
    setSavedVehicles(dataManager.favoritesManager.getAll());
    setTestDrives(dataManager.testDriveManager.getAll());
    setLeaseApplications(dataManager.leaseManager.getAll());
  }, []);

  const addVehicle = useCallback((vehicle) => {
    const newVehicle = dataManager.vehicleManager.add(vehicle);
    setVehicles(dataManager.vehicleManager.getAll());
    return newVehicle;
  }, []);

  const updateVehicle = useCallback((id, updatedVehicle) => {
    dataManager.vehicleManager.update(id, updatedVehicle);
    setVehicles(dataManager.vehicleManager.getAll());
  }, []);

  const deleteVehicle = useCallback((id) => {
    dataManager.vehicleManager.delete(id);
    setVehicles(dataManager.vehicleManager.getAll());
  }, []);

  const addInquiry = useCallback((inquiry) => {
    dataManager.inquiryManager.add(inquiry);
    setInquiries(dataManager.inquiryManager.getAll());
  }, []);

  const addSavedVehicle = useCallback((vehicle) => {
    if (user) {
      dataManager.favoritesManager.add({
        customerEmail: user.email,
        vehicleId: vehicle.id,
        vehicle: vehicle
      });
      setSavedVehicles(dataManager.favoritesManager.getAll());
    }
  }, [user]);

  const removeSavedVehicle = useCallback((vehicleId) => {
    if (user) {
      dataManager.favoritesManager.remove(user.email, vehicleId);
      setSavedVehicles(dataManager.favoritesManager.getAll());
    }
  }, [user]);

  const addTestDrive = useCallback((testDrive) => {
    dataManager.testDriveManager.add(testDrive);
    setTestDrives(dataManager.testDriveManager.getAll());
  }, []);

  const addLeaseApplication = useCallback((application) => {
    dataManager.leaseManager.add(application);
    setLeaseApplications(dataManager.leaseManager.getAll());
  }, []);

  const logout = useCallback(() => {
    dataManager.userManager.logout();
    setUser(null);
  }, []);

  return (
    <Router basename={process.env.NODE_ENV === 'production' ? '/dream-vehicle-website' : ''}>
      <div className="App">
        <Navbar user={user} onLogout={logout} />
        <WhatsAppFloatingButton />

        <Suspense fallback={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'50vh',fontSize:'1.2rem',color:'#666'}} aria-label="Loading content">⚡ Loading...</div>}>
          <Routes>
            <Route path="/" element={<Homepage vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/inventory" element={<VehicleList vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/vehicle/:id" element={<VehicleDetails vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} onTestDrive={addTestDrive} onLeaseApplication={addLeaseApplication} />} />
            <Route path="/cash-cars" element={<CashCars vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/lease-cars" element={<LeaseCars vehicles={vehicles} user={user} onInquiry={addInquiry} onAddSaved={addSavedVehicle} />} />
            <Route path="/lease-calculator" element={<LeaseCalculator />} />
            <Route path="/favorites" element={<Favorites user={user} onRemoveSaved={removeSavedVehicle} refreshData={refreshData} />} />
            <Route path="/test-drive" element={<TestDrive vehicles={vehicles} user={user} onTestDrive={addTestDrive} />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login setUser={setUser} />} />
            <Route path="/register" element={<Register setUser={setUser} />} />
            <Route path="/admin" element={user?.role === 'admin' ? <AdminPanel vehicles={vehicles} inquiries={inquiries} testDrives={testDrives} leaseApplications={leaseApplications} onAdd={addVehicle} onUpdate={updateVehicle} onDelete={deleteVehicle} refreshData={refreshData} /> : <Login setUser={setUser} />} />
            <Route path="/dashboard" element={user ? <CustomerDashboard user={user} inquiries={inquiries.filter(i => i.customerEmail === user.email)} savedVehicles={savedVehicles} testDrives={testDrives.filter(t => t.customerEmail === user.email)} leaseApplications={leaseApplications.filter(l => l.customerEmail === user.email)} onAddSaved={addSavedVehicle} onRemoveSaved={removeSavedVehicle} /> : <Login setUser={setUser} />} />
          </Routes>
        </Suspense>
        
        <ConditionalFooter />
      </div>
    </Router>
  );
}

export default App;